import IndicadorCalor from "./IndicadorCalor";

const BORDER = "1px solid #999";
const BORDER_GRUPO = "2px solid #aaa";

const COLORES_BG = {
  verde: "#d4edda",
  amarillo: "#fff3cd",
  rojo: "#f8d7da",
};

const CELL = {
  border: BORDER,
  fontSize: ".8rem",
  textAlign: "left",
  padding: "8px 7px",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  whiteSpace: "normal",
  overflow: "hidden",
};

const fmtDate = (lastDate) => {
  if (!lastDate) return null;
  const d = new Date(lastDate);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const COLS = 6;
const EMPTY_BG = "#f0f0f0";

// ─── Celda de contacto ───────────────────────────────────────────────────────
const ContactoCell = ({ contacto, rowSpan, style }) => {
  if (!contacto) {
    return (
      <td rowSpan={rowSpan || 1} style={{ ...CELL, backgroundColor: EMPTY_BG, color: "#aaa", ...style }}>
        —
      </td>
    );
  }
  const bg = COLORES_BG[contacto.color] || COLORES_BG.rojo;
  const fecha = fmtDate(contacto.lastDate);
  return (
    <td rowSpan={rowSpan || 1} style={{ ...CELL, backgroundColor: bg, verticalAlign: "middle", ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <IndicadorCalor color={contacto.color} size={8} />
        <span style={{ fontWeight: 500 }}>
          {contacto.nombre} {contacto.apellido}
        </span>
        {fecha && <span style={{ fontSize: ".65rem", color: "#666", marginLeft: "auto" }}>{fecha}</span>}
      </div>
    </td>
  );
};

// ─── Helpers para extraer contactos ──────────────────────────────────────────
const getContacto = (prod, role) => {
  const contactos = prod?.contactosEquipo || [];
  if (role === "gerente") return contactos.find((c) => c.rolIndex <= 1) || null;
  if (role === "jefe") return contactos.find((c) => c.rolIndex >= 2 && c.rolIndex <= 4) || null;
  if (role === "supervisor") return contactos.find((c) => c.cargo === "Supervisor") || null;
  return contactos.find((c) => c.cargo === "Asistente") || null;
};

const getBg = (color) => COLORES_BG[color] || COLORES_BG.rojo;

// ─── Filas de una empresa ────────────────────────────────────────────────────
const EmpresaRows = ({ emp }) => {
  const isSingle = emp.totalProductos === 1;

  if (isSingle) {
    const prod = emp.productos[0];
    const showProducto = prod.producto && prod.producto !== prod.empresa;

    return (
      <tr style={{ borderTop: BORDER_GRUPO }}>
        <td
          colSpan={showProducto ? 1 : 2}
          style={{
            ...CELL,
            backgroundColor: getBg(emp.color),
            fontWeight: 700,
            fontSize: ".8rem",
            verticalAlign: "middle",
            lineHeight: 1.2,
          }}
        >
          {emp.nombre}
          {prod.categoria_cliente && (
            <span style={{ fontSize: ".6rem", fontWeight: 600, color: "#555", marginLeft: 4 }}>
              {prod.categoria_cliente}
            </span>
          )}
        </td>
        {showProducto && (
          <td
            style={{ ...CELL, verticalAlign: "middle", fontSize: ".75rem", backgroundColor: getBg(prod.colorCliente) }}
          >
            {prod.producto}
          </td>
        )}
        <ContactoCell contacto={getContacto(prod, "gerente")} style={{ borderLeft: BORDER_GRUPO }} />
        <ContactoCell contacto={getContacto(prod, "jefe")} />
        <ContactoCell contacto={getContacto(prod, "supervisor")} />
        <ContactoCell contacto={getContacto(prod, "asistente")} />
      </tr>
    );
  }

  // Empresa con múltiples productos: empresa a la izquierda con rowSpan
  const n = emp.totalProductos;

  // Para cada rol, verificar si todos los productos tienen el mismo contacto → mergear
  const mergeRole = (role) => {
    const ids = emp.productos.map((p) => getContacto(p, role)?.id ?? null);
    if (ids[0] === null) return false;
    return ids.every((id) => id === ids[0]);
  };

  const mergeGerente = mergeRole("gerente");
  const mergeJefe = mergeRole("jefe");
  const mergeSupervisor = mergeRole("supervisor");
  const mergeAsistente = mergeRole("asistente");

  return emp.productos.map((prod, idx) => {
    const isFirst = idx === 0;

    return (
      <tr key={`${emp.nombre}-${prod.id}`} style={isFirst ? { borderTop: BORDER_GRUPO } : undefined}>
        {isFirst && (
          <td
            rowSpan={n}
            style={{
              ...CELL,
              backgroundColor: getBg(emp.color),
              fontWeight: 700,
              fontSize: ".8rem",
              verticalAlign: "middle",
              lineHeight: 1.2,
            }}
          >
            {emp.nombre}
            <span style={{ fontSize: ".65rem", color: "#777", marginLeft: 5 }}>({n})</span>
          </td>
        )}
        <td
          style={{
            ...CELL,
            verticalAlign: "middle",
            fontSize: ".75rem",
            backgroundColor: getBg(prod.colorCliente),
          }}
        >
          {prod.producto || "—"}
          {prod.categoria_cliente && (
            <span style={{ fontSize: ".6rem", fontWeight: 600, color: "#555", marginLeft: 4 }}>
              {prod.categoria_cliente}
            </span>
          )}
        </td>
        {(mergeGerente ? isFirst : true) && (
          <ContactoCell
            contacto={getContacto(prod, "gerente")}
            rowSpan={mergeGerente ? n : 1}
            style={{ borderLeft: BORDER_GRUPO }}
          />
        )}
        {(mergeJefe ? isFirst : true) && (
          <ContactoCell contacto={getContacto(prod, "jefe")} rowSpan={mergeJefe ? n : 1} />
        )}
        {(mergeSupervisor ? isFirst : true) && (
          <ContactoCell contacto={getContacto(prod, "supervisor")} rowSpan={mergeSupervisor ? n : 1} />
        )}
        {(mergeAsistente ? isFirst : true) && (
          <ContactoCell contacto={getContacto(prod, "asistente")} rowSpan={mergeAsistente ? n : 1} />
        )}
      </tr>
    );
  });
};

// ─── Tabla (reutilizable para con/sin subcategoría) ──────────────────────────
const TablaBloque = ({ titulo, subtitulo, totalClientes, empresas }) => (
  <div
    style={{
      borderRadius: 12,
      marginBottom: 16,
      border: "1px solid #ccc",
      boxShadow: "0 1px 4px rgba(0,0,0,.08)",
    }}
  >
    <table
      className="table table-sm table-bordered align-middle mb-0"
      style={{ fontSize: ".85rem", borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}
    >
      <colgroup>
        <col style={{ width: "11%" }} />
        <col style={{ width: "11%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "14%" }} />
      </colgroup>
      <thead>
        <tr>
          <th
            colSpan={COLS}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#222",
              color: "#fff",
              fontWeight: 700,
              fontSize: ".8rem",
              padding: "10px 12px",
              letterSpacing: ".3px",
            }}
          >
            {titulo}
            {subtitulo && (
              <span style={{ fontWeight: 400, fontSize: ".75rem", marginLeft: 6, opacity: 0.85 }}>/ {subtitulo}</span>
            )}
            <span style={{ fontWeight: 400, fontSize: ".7rem", marginLeft: 8, opacity: 0.7 }}>
              {totalClientes} clientes
            </span>
          </th>
        </tr>
        <tr style={{ backgroundColor: "#f0f0f0" }}>
          {["Empresa", "Producto", "Gerente / Director", "Jefe", "Supervisor", "Asistente"].map((label, i) => (
            <th
              key={label}
              style={{
                ...CELL,
                fontWeight: 600,
                fontSize: ".7rem",
                padding: "8px 7px",
                position: "sticky",
                top: 37,
                zIndex: 10,
                backgroundColor: "#f0f0f0",
                borderBottom: "2px solid #aaa",
                ...(i === 2 ? { borderLeft: BORDER_GRUPO } : {}),
              }}
            >
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {empresas.map((emp) => (
          <EmpresaRows key={emp.nombre} emp={emp} />
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Tabla principal ─────────────────────────────────────────────────────────
const TablaCalorClientes = ({ data }) => {
  if (!data || data.length === 0) {
    return <div style={{ textAlign: "center", color: "#999", padding: 40 }}>Sin datos de clientes</div>;
  }

  return (
    <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 140px)" }}>
      {data.map((cat) =>
        cat.soloGeneral ? (
          <TablaBloque
            key={cat.id}
            titulo={cat.nombre}
            totalClientes={cat.totalClientes}
            empresas={cat.subcategorias[0].empresas}
          />
        ) : (
          cat.subcategorias.map((sub) => (
            <TablaBloque
              key={`${cat.id}-${sub.nombre}`}
              titulo={cat.nombre}
              subtitulo={sub.nombre}
              totalClientes={sub.totalClientes}
              empresas={sub.empresas}
            />
          ))
        )
      )}
    </div>
  );
};

export default TablaCalorClientes;
